import React, {useState, useEffect} from "react";
import { useProfile } from "../hook/profileHook.mjs";
import { useDialog } from '../../components/dialog/DialogProvider.mjs';
import PageShell from '../../components/PageShell.mjs';


const ProfilePage = () => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { alert } = useDialog();
  const {
    user,
    loading,
    error,
    updateProfile,
    changePassword,
    fetchProfile
  } = useProfile();

  const [editMode, setEditMode] = useState(false);
  const [passwordMode, setPasswordMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Dark mode state - initialize from localStorage
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });

  // Compact view for tables
  const [compactView, setCompactView] = useState(() => {
    const saved = localStorage.getItem('compactView');
    return saved === 'true';
  });

  // Default claim type preference
  const [defaultClaimType, setDefaultClaimType] = useState(() => {
    return localStorage.getItem('defaultClaimType') || 'perdiem';
  });

  // Auto-save drafts
  const [autoSave, setAutoSave] = useState(() => {
    const saved = localStorage.getItem('autoSave');
    return saved !== 'false'; // default to true
  });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    region: '',
    position: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply dark mode to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Save compact view preference
  useEffect(() => {
    localStorage.setItem('compactView', compactView);
    // Apply compact view class to document
    if (compactView) {
      document.documentElement.classList.add('compact-view');
    } else {
      document.documentElement.classList.remove('compact-view');
    }
  }, [compactView]);

  // Save default claim type preference
  useEffect(() => {
    localStorage.setItem('defaultClaimType', defaultClaimType);
  }, [defaultClaimType]);

  // Save auto-save preference
  useEffect(() => {
    localStorage.setItem('autoSave', autoSave);
  }, [autoSave]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const toggleCompactView = () => {
    setCompactView(prev => !prev);
  };

  const toggleAutoSave = () => {
    setAutoSave(prev => !prev);
  };

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        region: user.region || '',
        position: user.position || '',
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    const success = await updateProfile(formData);
    if (success) {
      setEditMode(false);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    const success = await changePassword(passwordData);
    if (success) {
      setPasswordMode(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  };


  // No band title: the page's own card already says whose profile this is.
  const shellProps = { width: '3xl' };

  if (loading) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center gap-4">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell {...shellProps} className="pb-16">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            {successMessage}
          </div>
        )}

        {/* Profile Information */}
        <div className="bg-white rounded-xl shadow-lg p-5 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Profile Information</h2>
            {!editMode && (
              <button
                className="min-h-[44px] px-4 py-2 text-stanford-red border border-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors"
                onClick={() => setEditMode(true)}
              >
                Edit Profile
              </button>
            )}
          </div>

          {editMode ? (
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">System Role</label>
                <input
                  type="text"
                  value={user?.role?.replace(/_/g, ' ') || 'User'}
                  disabled
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-100 text-gray-500 capitalize cursor-not-allowed"
                />
                <p className="text-xs text-gray-400 mt-1">Contact an administrator to change your role</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  placeholder="e.g. Project Manager, Analyst"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                <input
                  type="text"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                />
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                <button type="submit" className="min-h-[44px] px-6 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors">
                  Save Changes
                </button>
                <button
                  type="button"
                  className="min-h-[44px] px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  onClick={() => setEditMode(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4 sm:space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-0">
                <span className="sm:w-24 sm:shrink-0 text-gray-500 text-sm">Name:</span>
                <span className="text-gray-800">{user?.firstName} {user?.lastName}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-0">
                <span className="sm:w-24 sm:shrink-0 text-gray-500 text-sm">Email:</span>
                <span className="text-gray-800 min-w-0 break-words">{user?.email}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-0">
                <span className="sm:w-24 sm:shrink-0 text-gray-500 text-sm">Role:</span>
                <span className="text-gray-800 capitalize">{user?.role?.replace(/_/g, ' ') || 'User'}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-0">
                <span className="sm:w-24 sm:shrink-0 text-gray-500 text-sm">Position:</span>
                <span className="text-gray-800">{user?.position || '-'}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-0">
                <span className="sm:w-24 sm:shrink-0 text-gray-500 text-sm">Region:</span>
                <span className="text-gray-800">{user?.region}</span>
              </div>
            </div>
          )}
        </div>

        {/* Password Section - Hidden for OAuth users */}
        {user?.authProvider === 'local' && (
        <div className="bg-white rounded-xl shadow-lg p-5 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Password</h2>
            {!passwordMode && (
              <button
                className="min-h-[44px] px-4 py-2 text-stanford-red border border-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors"
                onClick={() => setPasswordMode(true)}
              >
                Change Password
              </button>
            )}
          </div>

          {passwordMode ? (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                />
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                <button type="submit" className="min-h-[44px] px-6 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors">
                  Update Password
                </button>
                <button
                  type="button"
                  className="min-h-[44px] px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  onClick={() => setPasswordMode(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="text-gray-500">
              <span>••••••••</span>
            </div>
          )}
        </div>
        )}

        {/* Settings Section */}
        <div className="bg-white rounded-xl shadow-lg p-5 sm:p-6 mb-4 sm:mb-6">
          <div className="border-b border-gray-200 pb-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </h2>
          </div>

          <div className="space-y-4">
            {/* Dark Mode Toggle */}
            <div className="flex items-center justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  {darkMode ? (
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-800">Dark Mode</p>
                  <p className="text-sm text-gray-500">Switch to dark theme for reduced eye strain</p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={darkMode}
                aria-label="Dark Mode"
                onClick={toggleDarkMode}
                className={`relative shrink-0 inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-stanford-red ${darkMode ? 'bg-stanford-red' : 'bg-gray-300'}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${darkMode ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>

            {/* Compact View Toggle */}
            <div className="flex items-center justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Compact View</p>
                  <p className="text-sm text-gray-500">Show more items with reduced spacing</p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={compactView}
                aria-label="Compact View"
                onClick={toggleCompactView}
                className={`relative shrink-0 inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-stanford-red ${compactView ? 'bg-stanford-red' : 'bg-gray-300'}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${compactView ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>

            {/* Auto-Save Toggle */}
            <div className="flex items-center justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Auto-Save Drafts</p>
                  <p className="text-sm text-gray-500">Automatically save your work as you type</p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={autoSave}
                aria-label="Auto-Save Drafts"
                onClick={toggleAutoSave}
                className={`relative shrink-0 inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-stanford-red ${autoSave ? 'bg-stanford-red' : 'bg-gray-300'}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${autoSave ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>

            {/* Default Claim Type */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Default Claim Type</p>
                  <p className="text-sm text-gray-500">Your preferred claim type on dashboard</p>
                </div>
              </div>
              <select
                value={defaultClaimType}
                onChange={(e) => setDefaultClaimType(e.target.value)}
                aria-label="Default Claim Type"
                className="w-full sm:w-auto min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-stanford-red focus:border-transparent"
              >
                <option value="perdiem">Per Diem</option>
                <option value="expense">Expense</option>
              </select>
            </div>
          </div>
        </div>

        {/* OAuth Provider Info */}
        {user?.authProvider && user.authProvider !== 'local' && (
          <div className="bg-white rounded-xl shadow-lg p-5 sm:p-6 mb-4 sm:mb-6">
            <div className="border-b border-gray-200 pb-4 mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Authentication</h2>
            </div>
            <div className="flex items-center gap-3">
              {user.authProvider === 'google' && (
                <>
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="text-gray-600">Signed in with Google</span>
                </>
              )}
              {user.authProvider === 'stanford' && (
                <>
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#8C1515" d="M12 2L2 7L12 12L22 7L12 2Z"/>
                    <path fill="#8C1515" d="M2 17L12 22L22 17"/>
                    <path fill="#8C1515" d="M2 12L12 17L22 12"/>
                  </svg>
                  <span className="text-gray-600">Signed in with Stanford SSO</span>
                </>
              )}
            </div>
            {user.profilePicture && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-4">
                  <img
                    src={user.profilePicture}
                    alt="Profile"
                    className="w-16 h-16 rounded-full border-2 border-gray-200"
                  />
                  <span className="text-sm text-gray-500">Profile picture from {user.authProvider === 'google' ? 'Google' : 'Stanford'}</span>
                </div>
              </div>
            )}
          </div>
        )}
    </PageShell>
  );
};

export default ProfilePage;
